import React from 'react';

import { FontAwesomeIcon, IconButton } from '@schoolinks/ui-components';

import cn from 'classnames';

import { omit } from 'lodash';

import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import Snackbar from 'components/Common/Snackbar';

import { BACKDROP_CLICK } from 'constants/commonComponents';

import s from './Modal.css';

const Modal = props => {
  const actionsRef = React.useRef();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    title,
    children,
    actions,
    onClose,
    rootClassName,
    paperClassName,
    dialogActionsClassName,
    mobileHeaderClassName,
    desktopHeaderClassName,
    fullScreenClassName,
    showCloseButton,
    childrenClassName = '',
    isNotScrollable = false,
    disableEscapeKeyDown,
    noTitleBar,
    customHeader,
    scrollRef,
    open,
  } = props;

  const dialogHeader = fullScreen ? (
    <div className={cn(s.mobileHeader, mobileHeaderClassName)}>
      {customHeader || (
        <>
          {showCloseButton ? (
            <IconButton
              aria-label="Close modal"
              className={s.backButton}
              data-testid="close-modal-button"
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faCaretDown} />
            </IconButton>
          ) : null}
          <div className={s.title}>{title}</div>
        </>
      )}
    </div>
  ) : (
    <div className={cn(s.header, desktopHeaderClassName)}>
      {customHeader || (
        <>
          <div className={s.title}>{title}</div>
          {showCloseButton ? (
            <IconButton
              aria-label="Close modal"
              className={s.topRightCloseButton}
              data-testid="close-modal-button"
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
          ) : (
            <div />
          )}
        </>
      )}
    </div>
  );

  const mobileBodyOffset = actionsRef.current?.clientHeight;

  return (
    <Dialog
      fullScreen={fullScreen}
      classes={{
        container: s.container,
        root: cn(s.root, rootClassName),
        paper: cn(
          s.paper,
          {
            [s.mobilePaper]: fullScreen,
            [s.notScrollable]: isNotScrollable,
            // fullScreenClassName allows you to dynamically apply fullScreen styles to this modal's children
            // from your component's css by targeting descendants of the fullScreenClassName you provide.
            [fullScreenClassName]: fullScreen,
          },
          paperClassName,
        ),
      }}
      {...omit(props, 'title')}
      onClose={(e, reason) => {
        if (reason === BACKDROP_CLICK) {
          return;
        }
        onClose(e);
      }}
      open={open}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      <>
        {noTitleBar ? null : dialogHeader}
        <div
          style={
            fullScreen
              ? {
                  overflowY: 'auto',
                  maxHeight: `calc(100vh - ${mobileBodyOffset}px)`,
                  marginBottom: mobileBodyOffset,
                }
              : {}
          }
          className={cn(childrenClassName, {
            [s.scrollable]: !isNotScrollable,
          })}
          ref={scrollRef}
        >
          {children}
          {actions && (
            <DialogActions
              ref={actionsRef}
              className={cn(s.actions, dialogActionsClassName, {
                [s.mobileActions]: fullScreen,
              })}
            >
              {actions}
            </DialogActions>
          )}
        </div>
      </>
      <Snackbar />
    </Dialog>
  );
};

Modal.propTypes = Dialog.propTypes;

Modal.defaultProps = {
  showCloseButton: true,
  fullScreen: false,
  disableEscapeKeyDown: false,
  noTitleBar: false,
  customHeader: null,
  scrollRef: undefined,
};

export default Modal;
